/**
 * questionVisibility.js — catalog 3.7 show rules (Dr. Rami 2026-10-11, original-areas build).
 *
 * A question or an option may carry a rule; with no rule it always shows (every catalog before 3.7).
 *   question.showIf          { sex: 'female' | 'male' } · { minAge: 50 } · { question: '<answerKey>', anyOf: [...] }
 *   question.optionShowIf    { '<option text>': { sex | minAge } }
 *
 * ★ An UNKNOWN fact never hides anything: no sex or no date of birth on the profile → the question shows.
 *   A pregnancy question shown to a man is a nuisance; a pregnancy question hidden from a woman is a safety gap.
 * ★ `question` rules read the answer key in the same category (e.g. 'signs_symptoms_q2'): the follow-up shows only
 *   while one of `anyOf` is selected. A follow-up that hides drops its answer when the screen is saved
 *   (dropHiddenAnswers), so a case never carries an answer to a question the patient could no longer see.
 * The patient app is the only reader; the MD panel shows whatever was saved.
 */

// Age from a date of birth (YYYY-MM-DD, MM/DD/YYYY or anything Date parses). No imports, so the node gate can load it.
function ageFrom(dob) {
  if (!dob) return null
  const s = String(dob).trim()
  let d = null
  let m = s.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (m) d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  else if ((m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/))) d = new Date(Number(m[3]), Number(m[1]) - 1, Number(m[2]))
  else { const t = new Date(s); d = Number.isNaN(t.getTime()) ? null : t }
  if (!d || Number.isNaN(d.getTime())) return null
  const now = new Date()
  let age = now.getFullYear() - d.getFullYear()
  const mo = now.getMonth() - d.getMonth()
  if (mo < 0 || (mo === 0 && now.getDate() < d.getDate())) age--
  return age >= 0 && age < 130 ? age : null
}

export function patientFacts(profile) {
  const id = profile?.identification || {}
  const raw = String(id.sex || profile?.sex || profile?.gender || '').trim().toLowerCase()
  const sex = raw === 'f' || raw === 'female' ? 'female' : raw === 'm' || raw === 'male' ? 'male' : null
  const dob = id.dob || id.dateOfBirth || profile?.dob || profile?.dateOfBirth
  return { sex, age: ageFrom(dob) }
}

const asList = (v) => (Array.isArray(v) ? v : typeof v === 'string' && v ? [v] : [])

export function ruleAllows(rule, facts, answers) {
  if (!rule) return true
  if (rule.sex && facts?.sex && rule.sex !== facts.sex) return false
  if (rule.minAge != null && facts?.age != null && facts.age < rule.minAge) return false
  if (rule.question) {
    const picked = asList(answers?.[rule.question])
    if (!picked.some((x) => (rule.anyOf || []).includes(x))) return false
  }
  return true
}

export const isQuestionShown = (question, facts, answers) => ruleAllows(question?.showIf, facts, answers)

export const visibleOptions = (question, facts) =>
  (question?.options || []).filter((o) => ruleAllows(question?.optionShowIf?.[o], facts, null))

/** answers without the keys of questions (in this category) that are hidden right now, or options now hidden. */
export function dropHiddenAnswers(answers, questions, categoryKey, facts) {
  const out = { ...(answers || {}) }
  for (const q of questions || []) {
    const k = `${categoryKey}_${q.id}`
    if (!(k in out)) continue
    if (!isQuestionShown(q, facts, answers)) { delete out[k]; continue }
    if (q.optionShowIf && Array.isArray(out[k])) {
      const ok = new Set(visibleOptions(q, facts))
      out[k] = out[k].filter((o) => ok.has(o))
    }
  }
  return out
}
